'use client';

import { useEffect, useState } from 'react';

/**
 * useInstantMotion — the single gate that decides whether Labs motion should be SUPPRESSED (rendered
 * at its settled end-state, no animation).
 *
 * Motion is suppressed when EITHER:
 *   • the user prefers reduced motion (`prefers-reduced-motion: reduce`), OR
 *   • we're under test automation — Playwright (`navigator.webdriver`) or the dev `__labsNoAuto`
 *     flag — so the visual-regression baselines stay deterministic (every transition renders its
 *     final frame instantly).
 *
 * Returns `true` ⇒ render instant/settled; `false` ⇒ animate.
 *
 * SSR-safe: starts `true` (instant) on the server and the first client paint, then resolves on mount.
 * Starting instant avoids a hydration flash AND keeps automation deterministic even before the effect
 * runs.
 */
function isAutomated(): boolean {
  const w = window as unknown as { __labsNoAuto?: boolean };
  return Boolean(w.__labsNoAuto) || (typeof navigator !== 'undefined' && navigator.webdriver === true);
}

export function useInstantMotion(): boolean {
  const [instant, setInstant] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    setInstant((mq?.matches ?? false) || isAutomated());

    // Track live changes to the reduced-motion preference (automation flags don't change at runtime).
    if (!mq) {
      return;
    }
    const onChange = (): void => setInstant(mq.matches || isAutomated());
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  return instant;
}
